import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { formatCurrency } from "@/utils/tools";
import type { Transaction } from "@/utils/types";
import type { Account } from "@aircap/common/account";
import type { BridgeTransaction } from "@aircap/common/bridge";
import { categories as bridgeCategories, mapBridgeCategoryId } from "@aircap/common/bridge";
import { collection, doc, orderBy, query, updateDoc, where } from "firebase/firestore";
import { computed, type Ref } from "vue";
import { type VueFirestoreDocumentData } from "vuefire";

const bridgeCategoriesById = new Map(bridgeCategories.map((c) => [c.id, c]));

export const useBridgeTransactions = (
  account: Ref<VueFirestoreDocumentData<Account> | undefined>,
  filters?: {
    beneficiaryIban?: Ref<string | undefined>; // Not compatible with open banking provider
    isFuture?: Ref<boolean>;
  },
) => {
  const { locale } = useI18n();
  const transactionsSource = computed(() => {
    if (!account.value?.bankAccountId) return null;

    if (filters?.beneficiaryIban?.value)
      throw new Error("Beneficiary Iban filter is not compatible with Bridge transactions");

    return query(
      collection<BridgeTransaction>(
        db,
        `accounts/${account.value?.id}/bridgeAccounts/${account.value?.bankAccountId}/transactions`,
      ),
      where<BridgeTransaction>("show_client_side", "==", true),
      where<BridgeTransaction>("is_deleted", "==", false),
      ...(filters?.isFuture?.value === false
        ? [where<BridgeTransaction>("is_future", "==", false)]
        : []),
      ...(filters?.isFuture?.value === true
        ? [where<BridgeTransaction>("is_future", "==", true)]
        : []),
      orderBy<BridgeTransaction>("date", "desc"),
    );
  });

  const { data: transactionCollection, pending: loading } =
    useCollection<BridgeTransaction>(transactionsSource);

  const transactions = computed<Transaction[]>(() =>
    transactionCollection.value.map(
      (t) =>
        ({
          date: t.date,
          id: t.id,
          name: t.clean_description,
          amount: t.amount,
          category: bridgeCategoriesById.get(
            t.userCategoryId ?? mapBridgeCategoryId(t.category_id),
          ),
          trackedCategorySplits: t.trackedCategorySplits,
          suggestedTrackedCategoryId: t.suggestedTrackedCategoryId,
          rejectedSuggestedTrackedCategoryId: t.rejectedSuggestedTrackedCategoryId,
          currencyCode: t.currency_code,
          amountFormatted: formatCurrency({
            value: t.amount,
            currencyCode: t.currency_code,
            sign: true,
            locale: locale.value,
          }),
          is_future: t.is_future,
          is_flagged: t.is_flagged,
          dateFormatted: {
            short: new Intl.DateTimeFormat(locale.value).format(new Date(t.date)),
            long: new Intl.DateTimeFormat(locale.value, {
              dateStyle: "long",
              timeStyle: "short",
            }).format(new Date(t.date)),
          },
        }) satisfies Transaction,
    ),
  );

  const flagTransaction = (transactionId: string, value: boolean) => {
    if (!account.value?.id || !account.value.bankAccountId) return;
    const transactionRef = doc<BridgeTransaction>(
      db,
      `accounts/${account.value.id}/bridgeAccounts/${account.value.bankAccountId}/transactions`,
      transactionId,
    );

    void updateDoc(transactionRef, { is_flagged: value });
  };

  return {
    transactions,
    loading,
    flagTransaction,
  };
};
